/**
 * Color Palette definitions for children's book illustrations.
 *
 * This file contains both FE-safe data (title, description, emoji, colors) and BE-only data (prompts).
 * - FE should import: COLOR_PALETTE_OPTIONS, COLOR_PALETTE_DISPLAY
 * - BE should import: COLOR_PALETTES (includes prompts)
 */

/**
 * Color palette keys - used for type safety and as enum values.
 */
export const COLOR_PALETTE_KEYS = [
  'classic_neutral',
  'rainbow_bright',
  'soft_pastel_dreams',
  'enchanted_forest',
  'ocean_adventure',
  'sunset_magic',
  'berry_sweet',
  'cozy_autumn',
  'space_galaxy',
] as const;

export type ColorPaletteKey = (typeof COLOR_PALETTE_KEYS)[number];

/**
 * Full color palette definition including prompts (BE use only).
 */
export interface ColorPaletteDefinition {
  /** Display title for the palette */
  title: string;
  /** User-friendly description */
  description: string;
  /** Emoji icon for UI display */
  emoji: string;
  /** AI prompt fragment for image generation (BE only) */
  palettePrompt: string;
  /** Representative hex colors for the palette (FE-safe for swatches) */
  hexColors: string[];
}

/**
 * FE-safe display data (no prompts).
 */
export interface ColorPaletteDisplay {
  title: string;
  description: string;
  emoji: string;
  hexColors: string[];
}

/**
 * Complete color palette definitions with prompts.
 * Use this on the backend for image generation.
 */
export const COLOR_PALETTES: Record<ColorPaletteKey, ColorPaletteDefinition> = {
  classic_neutral: {
    title: 'Classic Neutral',
    description:
      'Soft, timeless palette with warm creams, gentle grays, and muted tones. Sophisticated and calming, perfect for bedtime stories and classic tales. Works well with any illustration style.',
    emoji: '🤍',
    palettePrompt:
      'soft neutral color palette, warm cream background, gentle gray tones, muted beige, soft white accents, warm brown details, subtle sepia undertones, timeless sophisticated colors, calming atmosphere,',
    hexColors: ['#FDFBF7', '#E8E0D8', '#C9BFB1', '#8B7355', '#5D4E37'],
  },

  rainbow_bright: {
    title: 'Rainbow Bright',
    description:
      "Vibrant, energetic palette with all primary and secondary colors at full saturation. Joyful and exciting, captures children's attention immediately. Best for playful, active stories.",
    emoji: '🌈',
    palettePrompt:
      'vibrant rainbow color palette, bright primary colors, saturated red yellow blue, energetic green orange purple, cheerful vivid tones, bold contrasting colors, playful celebration colors, maximum color saturation,',
    hexColors: [
      '#FF6B6B',
      '#FFD93D',
      '#6BCB77',
      '#4D96FF',
      '#9B59B6',
      '#FF9F43',
    ],
  },

  soft_pastel_dreams: {
    title: 'Soft Pastel Dreams',
    description:
      'Gentle, dreamy pastels with lavender, mint, peach, and soft blue. Calming and sweet, ideal for baby books, lullabies, and gentle stories. Creates a soothing atmosphere.',
    emoji: '💜',
    palettePrompt:
      'soft pastel color palette, gentle lavender, mint green, peach pink, baby blue, cream white, dreamy soft tones, muted delicate colors, sweet nursery aesthetic, calming gentle atmosphere,',
    hexColors: ['#E8DCF0', '#C8E6D8', '#F5D8C8', '#D8E8F5', '#FFF5F0'],
  },

  enchanted_forest: {
    title: 'Enchanted Forest',
    description:
      'Rich, earthy greens with warm browns, golden accents, and deep teal. Natural and magical, perfect for woodland adventures and nature stories. Grounding yet whimsical.',
    emoji: '🌲',
    palettePrompt:
      'enchanted forest color palette, deep forest green, rich moss tones, warm brown bark, golden sunlight accents, deep teal shadows, earthy natural colors, woodland magical atmosphere, organic nature palette,',
    hexColors: ['#2D5A27', '#6B8E4E', '#8B6914', '#C9A227', '#1A3A3A'],
  },

  ocean_adventure: {
    title: 'Ocean Adventure',
    description:
      'Cool aquatic blues ranging from pale seafoam to deep navy, with coral and sand accents. Fresh and invigorating, ideal for underwater stories and beach adventures.',
    emoji: '🐚',
    palettePrompt:
      'ocean adventure color palette, seafoam turquoise, deep navy blue, coral pink accents, sandy beige, wave white foam, aquatic cool tones, underwater paradise colors, beach summer atmosphere,',
    hexColors: ['#7FDBDA', '#3B5998', '#FF6F61', '#F5E6D3', '#003366'],
  },

  sunset_magic: {
    title: 'Sunset Magic',
    description:
      'Warm gradient palette from golden yellow through orange to soft purple. Romantic and adventurous, creates beautiful atmospheric lighting effects. Perfect for emotional moments.',
    emoji: '🌅',
    palettePrompt:
      'sunset magic color palette, golden yellow warmth, orange glow, soft coral pink, lavender purple twilight, warm gradient tones, magical golden hour colors, romantic evening atmosphere,',
    hexColors: ['#FFD700', '#FF8C42', '#FF6B6B', '#C792EA', '#7B68EE'],
  },

  berry_sweet: {
    title: 'Berry Sweet',
    description:
      'Rich berry tones with magenta, plum, raspberry, and soft pink. Playful and bold, with a touch of sophistication. Great for stories featuring food, gardens, or strong characters.',
    emoji: '🍇',
    palettePrompt:
      'berry sweet color palette, rich magenta pink, deep plum purple, raspberry red, soft rose pink, blueberry accent, sweet fruity tones, playful bold colors, garden berry atmosphere,',
    hexColors: ['#E91E8C', '#8E4585', '#E30B5D', '#FFB6C1', '#4F86F7'],
  },

  cozy_autumn: {
    title: 'Cozy Autumn',
    description:
      'Warm harvest palette with pumpkin orange, golden yellow, burgundy, and warm brown. Nostalgic and comfortable, perfect for seasonal stories and home-centered narratives.',
    emoji: '🍂',
    palettePrompt:
      'cozy autumn color palette, pumpkin orange warmth, golden yellow harvest, deep burgundy red, warm cinnamon brown, cream background, fall harvest tones, comfortable nostalgic colors, cozy home atmosphere,',
    hexColors: ['#FF7518', '#FFB347', '#800020', '#8B4513', '#FFF8DC'],
  },

  space_galaxy: {
    title: 'Space Galaxy',
    description:
      'Deep dramatic palette with rich navy blue, vibrant purple, bright teal, and golden accents. Bold and mysterious, ideal for adventure stories and nighttime scenes. Creates wonder and imagination.',
    emoji: '🚀',
    palettePrompt:
      'deep navy blue as primary color, rich purple tones, bright teal accents, pure white highlights, golden yellow details, dramatic dark background with vibrant accent colors, high contrast color scheme,',
    hexColors: ['#0D1B2A', '#7B2D8E', '#00CED1', '#FFFFFF', '#FFD700'],
  },
};

/**
 * Color palette options array for schema validation.
 * Use this in Zod schemas and for iteration.
 */
export const COLOR_PALETTE_OPTIONS = COLOR_PALETTE_KEYS;

/**
 * FE-safe display data without prompts.
 * Use this on the frontend for showing options to users.
 */
export const COLOR_PALETTE_DISPLAY: Record<
  ColorPaletteKey,
  ColorPaletteDisplay
> = Object.fromEntries(
  COLOR_PALETTE_KEYS.map((key) => [
    key,
    {
      title: COLOR_PALETTES[key].title,
      description: COLOR_PALETTES[key].description,
      emoji: COLOR_PALETTES[key].emoji,
      hexColors: COLOR_PALETTES[key].hexColors,
    },
  ])
) as Record<ColorPaletteKey, ColorPaletteDisplay>;

/**
 * Get color palette prompt by key (BE use only).
 */
export function getColorPalettePrompt(key: ColorPaletteKey): string {
  return COLOR_PALETTES[key].palettePrompt;
}

/**
 * Get color palette display data by key (FE-safe).
 */
export function getColorPaletteDisplay(
  key: ColorPaletteKey
): ColorPaletteDisplay {
  return COLOR_PALETTE_DISPLAY[key];
}
